import { CommonModule } from "@angular/common";
import { RouterModule } from "@angular/router";
import { HeaderComponent } from "./header/header.component";
import { WebddnCartComponent } from "./webddncart/webddncart.component";
import { OfflineBannerComponent } from "./offline/offline-banner.component";
import { FooterComponent } from "./footer/footer.component";
// app.component.ts
import { Component, OnInit, OnDestroy, AfterViewInit, ElementRef, Injector, NgZone, viewChild, computed, signal } from '@angular/core';
import { HeaderSettingsService } from './services/header-settings.service';
import { DraftService } from './draft.service';
import { CookieService } from 'ngx-cookie-service';
import { Router, NavigationEnd, NavigationStart, NavigationCancel, NavigationError, RouteConfigLoadStart, RouteConfigLoadEnd, ActivatedRoute } from '@angular/router';
import { Title, Meta } from '@angular/platform-browser';
import { filter, takeUntil, catchError } from 'rxjs/operators';
import { Subject, of, fromEvent } from 'rxjs';
import { environment } from 'src/environments/environment';
import { webddn } from 'src/environments/webddn';
import { apikeys } from 'src/environments/apikeys';
import { getCurrentAppType } from './shared/utils/app-type-helper';
import { content } from 'src/environments/data';
import { FontService } from './services/font.service';
import { ApiService } from './services/api.service';
import { LoadingService } from './services/loading.service';
import { DynamicRouteService } from './services/dynamic-route.service';
import { BodyClassService } from './services/body-class.service';
import { UserService } from './services/user.service';
import { fonts } from 'src/environments/fonts';
import { DraftUser } from './shared/interfaces/draftuser.interface';
import { User } from './shared/interfaces/user.interface';
import { isHomeColdLoad, scheduleIdleTask } from './shared/utils/schedule-idle';
import { isHybridIosShell, isHybridShell, requestNativeHybridSignOut } from './shared/hybrid-shell';
import { parseDdnSession } from './utils/ddn-session';
import { SocketIoService } from './services/socket-io.service';

interface VM {
  date: string;
}

interface CustomFont {
  _id?: string;
  id?: string;
  name: string;
  originalName: string;
  fileName?: string;
  mimeType?: string;
  size?: number;
  uploadDate?: string;
  userId?: string;
  url?: string; // URL for the loaded font
  family?: string; // Font family name for CSS
  isLoading?: boolean; // Track loading state
}

declare const gtag: Function;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [CommonModule, RouterModule, HeaderComponent, WebddnCartComponent, OfflineBannerComponent, FooterComponent]
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly isOffline = signal(typeof navigator !== 'undefined' ? !navigator.onLine : false);
  moduleTotal = 0;
  moduleLoaded = 0;
  webddnTitle = content.WEBDDN_TITTLE;

  isMobileView = signal(typeof window !== 'undefined' ? window.innerWidth < 768 : false);
  loadHeader = signal(typeof window !== 'undefined' ? window.innerWidth >= 768 : true);

  user: DraftUser | null = null;
  draftUser: DraftUser | null = null;
  currentUser: User | null = null;
  vm: VM | undefined;
  loadingDataImg = false;
  customFonts: CustomFont[] = [];
  startTime = performance.now();
  app_type = getCurrentAppType()?.type ?? webddn.APP_TYPE;
  // Header property for template usage - now reactive
  header = {
    authentication: {
      user: false
    }
  };

  readonly headerEl = viewChild.required<ElementRef<HTMLElement>>('headerEl');
  headerTall = false;
  private resizeObserver?: ResizeObserver;

  readonly headerSettings = this.headerSettingsService.currentSettings;
  readonly isHeaderFixed = computed(() => {
    if (!this.headerSettings().stickyHeader) {
      return false;
    }
    return !this.headerTall;
  });

  // Subject for cleanup
  private destroy$ = new Subject<void>();

  // Track if current route is homepage for conditional breadcrumb display
  isHomepage = false;
  isUnauthorized = false;

  // ✅ REMOVED: private hasInitializedDynamicRoutes = false;

  constructor(
    private headerSettingsService: HeaderSettingsService,
    private dynamicRouteService: DynamicRouteService,
    private draftService: DraftService,
    private cookieService: CookieService,
    private metaService: Meta,
    private apiService: ApiService,
    private loadingService: LoadingService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private fontService: FontService,
    private bodyClassService: BodyClassService,
    private userService: UserService,
    private ngZone: NgZone,
    private injector: Injector,
    private socketIoService: SocketIoService
  ) {
    this.setupRouterEvents();
    this.setupTitleAndMetaUpdates();
  }

  ngAfterViewInit(): void {
    if (typeof ResizeObserver !== 'undefined' && this.headerSettings().stickyHeader) {
      this.resizeObserver = new ResizeObserver(entries => {
        const height = entries[0]?.contentRect.height ?? 0;
        const tall = height > 53;
        if (tall !== this.headerTall) {
          this.headerTall = tall;
        }
      });
      const headerEl = this.headerEl()?.nativeElement;
      if (headerEl) {
        this.resizeObserver.observe(headerEl);
      }
    }
  }

  // ✅ FIXED: Remove dynamic route initialization - APP_INITIALIZER handles it
  ngOnInit(): void {
    console.log('🚀 AppComponent: Starting initialization...');
    if (typeof window !== 'undefined') {
      window.addEventListener('offline', () => this.isOffline.set(true));
      window.addEventListener('online', () => this.isOffline.set(false));
      
      if (this.isMobileView()) {
        scheduleIdleTask(() => this.loadHeader.set(true), 3000);
      }
    }
    // Local ↔ prod Android packages bake different API hosts; clear stale session.
    this.clearSessionIfApiHostChanged();

    // Setup authentication state subscription
    this.setupAuthenticationSubscription();
    this.setupNativeHybridSession();

    this.preloadAssets();

    if (this.dynamicRouteService.areRoutesLoaded()) {
      console.log('✅ AppComponent: Dynamic routes already loaded by APP_INITIALIZER');
    } else {
      console.log('ℹ️ AppComponent: Dynamic routes not loaded (static routes only)');
    }

    scheduleIdleTask(() => this.loadCustomFonts(), isHomeColdLoad(this.router.url) ? 6000 : 500);
    this.setupUserTracking();
    if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
      window.scrollTo(0, 0);
    }

    // Set initial body class based on current route
    this.handleBodyHeroClass(this.router.url);

    // Dexie/outbox sync after first paint — avoid pinning OfflineSync in constructor DI.
    this.scheduleOfflineSyncBootstrap();

    console.log('✅ AppComponent: Initialization complete');
  }

  /**
   * Lazy-construct OfflineSyncService (and Dexie outbox drain) after paint.
   * Hybrid shells start on the next tick; browsers use requestIdleCallback.
   */
  private scheduleOfflineSyncBootstrap(): void {
    if (typeof window === 'undefined') return;

    const start = () => {
      void import('./offline/offline-sync.service').then(({ OfflineSyncService }) => {
        this.injector.get(OfflineSyncService);
      });
    };

    if (isHybridShell()) {
      setTimeout(start, 0);
      return;
    }

    const isMobile = window.innerWidth <= 768;

    if (isHomeColdLoad(this.router.url)) {
      scheduleIdleTask(() => start(), isMobile ? 20000 : 10000);
      return;
    }

    const ric = (
      window as Window & {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      }
    ).requestIdleCallback;

    if (typeof ric === 'function') {
      ric(() => start(), { timeout: isMobile ? 10000 : 3000 });
    } else {
      setTimeout(start, isMobile ? 10000 : 1);
    }
  }

  /**
   * Sessions are API-host-specific (local Redis ≠ prod Redis).
   * When packaging switches LOCAL_API_DOMAIN / API_DOMAIN, drop the cookie and
   * native Keychain (via onSignOut) so hybrid shells return to native voice login.
   */
  private clearSessionIfApiHostChanged(): void {
    if (typeof localStorage === 'undefined') return;
    const key = 'webddn.sessionApiHost';
    const current = String(environment.API_DOMAIN || '')
      .replace(/^https?:\/\//, '')
      .replace(/\/.*$/, '')
      .trim()
      .toLowerCase();
    if (!current) return;

    const previous = (localStorage.getItem(key) || '').trim().toLowerCase();
    if (previous && previous !== current) {
      console.warn(
        `API host changed (${previous} → ${current}); clearing ddnsession for re-login`
      );
      this.cookieService.delete('ddnsession');
      // Native Keychain/session store — otherwise RootAuth keeps WebView up.
      requestNativeHybridSignOut();
    }
    localStorage.setItem(key, current);
  }

  /**
   * Setup authentication state subscription
   */
  private setupAuthenticationSubscription(): void {
    // Subscribe to user state changes
    this.userService.user$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(user => {
      this.currentUser = user;
      // Update header authentication state
      this.header.authentication.user = !!(user?.token);
      console.log('AppComponent: Authentication state changed', {
        hasUser: !!user,
        hasToken: !!(user?.token),
        headerAuthUser: this.header.authentication.user
      });
      // Trigger change detection
    });

    // Subscribe to auth status changes as well
    this.userService.authStatus$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(isAuthenticated => {
      this.header.authentication.user = isAuthenticated;
      console.log('AppComponent: Auth status changed', isAuthenticated);
    });

    // Initialize user data
    this.userService.refreshUserFromCookie();
  }

  /** Native hybrid shells (iOS WKWebView, Android WebView, Windows WebView2): native voice login injects ddnsession; apply like web setCookieAndNavigate. */
  private setupNativeHybridSession(): void {
    if (typeof window === 'undefined') {
      return;
    }
    const existing = parseDdnSession(this.cookieService.get('ddnsession'));
    if (existing?.token) {
      this.applyNativeDdnSession(existing, false);
    }
    fromEvent<CustomEvent>(window, 'webddn-native-session')
      .pipe(takeUntil(this.destroy$))
      .subscribe((ev) => {
        this.ngZone.run(() => this.applyNativeDdnSession(ev.detail, true));
      });
    fromEvent(window, 'webddn-native-session-cleared')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.ngZone.run(() => this.userService.clearUser());
      });
  }

  private applyNativeDdnSession(raw: unknown, writeCookie: boolean): void {
    let user: User | null = null;
    if (raw && typeof raw === 'object' && (raw as User).token) {
      user = { ...(raw as User) };
    } else if (typeof raw === 'string') {
      user = parseDdnSession(raw);
    }
    if (!user?.token) {
      return;
    }
    if (user.signup_complete === undefined) {
      user.signup_complete = true;
    }
    if (writeCookie) {
      const date = new Date();
      date.setTime(date.getTime() + 12 * 60 * 60 * 1000);
      this.cookieService.set('ddnsession', JSON.stringify(user), date, '/');
    }
    this.userService.updateUser(user, false);
    this.socketIoService.ensureConnected();
    if (writeCookie) {
      this.navigateAfterNativeSession(user);
    }
  }

  private navigateAfterNativeSession(user: User): void {
    const loginPath = '/' + (content.WEBDDN_LOGIN_PATH || 'login');
    const signupBase = '/' + (content.WEBDDN_SIGNUP_PATH || 'signup');
    const path = (typeof window !== 'undefined' ? window.location.pathname : '') || this.router.url || '/';
    if (user.signup_complete === false) {
      if (!path.startsWith(signupBase)) {
        const rolePath =
          user.role === 't'
            ? content.WEBDDN_TRANSPORTER || 'agent'
            : content.WEBDDN_PASSENGER || 'customer';
        this.router.navigate([content.WEBDDN_SIGNUP_PATH, rolePath]);
      }
      return;
    }
    if (
      (user.role === 'm' || user.role === 'b' || user.role === 't') &&
      user.is_active === false
    ) {
      this.router.navigate(['/profile']);
      return;
    }
    if ((user.role === 'b' || user.role === 't') && user.has_schedule === false) {
      this.router.navigate(['/my-schedule']);
      return;
    }
    const journey = content.WEBDDN_JOURNEY || 'appointment';
    if (path === '/' || path === loginPath || path.startsWith(loginPath + '/') || path.includes(loginPath)) {
      this.router.navigate(['/' + journey]);
    }
  }

  /**
   * Cleanup on component destruction
   */
  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupRouterEvents() {
    this.router.events.subscribe(event => {
      if (event instanceof RouteConfigLoadStart) {
        this.moduleTotal++;
        this.loadingService.setModuleProgress(this.moduleLoaded, this.moduleTotal);
      }
      if (event instanceof RouteConfigLoadEnd) {
        this.moduleLoaded++;
        this.loadingService.setModuleProgress(this.moduleLoaded, this.moduleTotal);
      }
      this.navigationInterceptor(event);
    });

    this.loadingService.progress$.pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (progress) => {
        const elapsed = ((performance.now() - this.startTime) / 1000).toFixed(2);
        console.log(`📊 Progress: ${progress}, Elapsed: ${elapsed}s`);
      },
      error: (error) => {
        console.error('Error in progress subscription:', error);
      }
    });

    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((event: any) => {
      if (typeof gtag !== 'undefined') {
        gtag('event', 'page_view', { page_path: event.urlAfterRedirects });
      }
      // Handle body-hero class for homepage
      this.handleBodyHeroClass(event.urlAfterRedirects);
    });
  }

  private setupTitleAndMetaUpdates() {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        const title = this.getTitle(this.router.routerState, this.router.routerState.root).join(' > ');
        const meta = this.getMeta(this.router.routerState, this.router.routerState.root).join(' > ');
        this.titleService.setTitle(title);
        this.metaService.updateTag({ name: 'description', content: meta });

        // Add noindex for private/authenticated routes
        const url = event.urlAfterRedirects.split('?')[0];
        const privatePrefixes = [
          '/admin', '/billing', '/billings', '/invoice-management', '/checkout',
          '/payment', '/paymentform', '/payment-confirmation', '/success',
          '/profile', '/kanban', '/my-schedule', '/notifications', '/team', '/teams',
          '/roster', '/registration', '/business-user', '/passenger', '/transporter',
          '/company', '/product', '/journey', '/subscription', '/content-list',
          '/content', '/scraper', '/patient-assessment', '/patient-intake',
          '/health-fitness', '/finance-life', '/publish', '/login', '/signup',
          '/signout', '/signinfo', '/signinfo2', '/forgot-password', '/reset-password',
          '/apikeys', '/sign-project', '/font-management', '/webddn-project',
          '/design-system', '/product-management', '/flat-rate', '/distance-matrix-cache',
          '/hcai', '/cases', '/fwdu', '/google-photos-callback'
        ];

        const shouldNoIndex = privatePrefixes.some(prefix => url.startsWith(prefix)) || 
                              this.getNoIndexData(this.router.routerState, this.router.routerState.root);

        if (shouldNoIndex) {
          this.metaService.updateTag({ name: 'robots', content: 'noindex' });
        } else {
          this.metaService.removeTag('name="robots"');
        }
      }
    });
  }

  private getNoIndexData(state: any, parent: any): boolean {
    if (parent?.snapshot?.data?.noindex) {
      return true;
    }
    if (state && parent) {
      const child = state.firstChild(parent);
      if (child) {
        return this.getNoIndexData(state, child);
      }
    }
    return false;
  }

  private configureGtag(config: Record<string, string | undefined>) {
    const gtagId = apikeys.gtag_config || apikeys.local_gtag_config;
    if (!gtagId || typeof gtag === 'undefined') {
      return;
    }
    gtag('config', gtagId, config);
  }

  private setupUserTracking() {
    if (typeof window === 'undefined') return;
    if (typeof Storage !== 'undefined') {
      const uuid = localStorage.getItem('UUID');
      const duid = localStorage.getItem('DUID');
      if (uuid) {
        this.user = { UUID: uuid };
        this.configureGtag({ user_id: this.user.UUID });
      } else if (duid) {
        this.draftUser = { DUID: duid };
        this.configureGtag({ user_id: this.draftUser.DUID });
      } else {
        this.createDraftUser();
      }
    } else {
      const uuid = this.cookieService.get('UUID');
      const duid = this.cookieService.get('DUID');
      if (uuid) {
        this.user = { UUID: uuid };
        this.configureGtag({ user_id: this.user.UUID });
      } else if (duid) {
        this.draftUser = { DUID: duid };
        this.configureGtag({ user_id: this.draftUser.DUID });
      } else {
        this.createDraftUser();
      }
    }
  }

  private createDraftUser() {
    this.draftService.getRequest(`${environment.BASEURL}${environment.DRAFTAPIURL}`).subscribe({
      next: (response: any) => {
        this.user = response;
        if (typeof Storage !== 'undefined') {
          localStorage.setItem('DUID', this.user?.DUID || '');
        } else {
          this.cookieService.set('DUID', this.user?.DUID || '');
        }
        this.configureGtag({
          user_id: this.user?.UUID,
          DUID: this.user?.DUID
        });
      },
      error: () => {}
    });
  }

  private loadCustomFonts() {
    if (typeof window === 'undefined') return;
    let fontsArray: CustomFont[] = [];
    const fontsObj = fonts as any;
    if (Array.isArray(fontsObj)) {
      fontsArray = fontsObj;
    } else if (fontsObj && Array.isArray(fontsObj.fonts)) {
      fontsArray = fontsObj.fonts;
    } else if (fontsObj && typeof fontsObj === 'object') {
      const firstArrayProp = Object.values(fontsObj).find(value => Array.isArray(value));
      if (firstArrayProp) fontsArray = firstArrayProp as CustomFont[];
    }

    this.customFonts = fontsArray.map(font => ({
      ...font,
      isLoading: true,
      family: `font-${font.id || font._id}`
    }));

    if (this.customFonts.length > 0) {
      this.customFonts.forEach(font => {
        // Try loading from assets first
        const fontPath = `assets/fonts/${font.originalName}`;
        this.fontService.loadFont(`font-${font.id || font._id}`, fontPath)
          .then(() => {
            const index = this.customFonts.findIndex(f => f.name === font.name);
            if (index !== -1) this.customFonts[index].isLoading = false;
            console.log(`Font loaded from assets: ${font.name}`);
          })
          .catch(error => {
            console.warn(`Failed to load font from assets: ${font.name}, trying API...`);
            // Fallback to API blob download
            this.loadFontFromApi(font);
          });
      });
    }
  }

  private loadFontFromApi(font: CustomFont): void {
    const fontId = font.id || font._id;

    this.apiService.getBlob(environment.BASEURL + environment.FONT_DOWNLOAD_APIURL + `/${fontId}/download`)
      .pipe(
        catchError(error => {
          console.error(`Error loading font from API for ${font.name}:`, error);
          return of(null);
        })
      )
      .subscribe(blob => {
        if (blob && typeof document !== 'undefined') {
          const fontUrl = URL.createObjectURL(blob);
          const fontFamily = `font-${fontId}`;

          // Create @font-face rule
          const style = document.createElement('style');
          style.innerHTML = `
            @font-face {
              font-family: '${fontFamily}';
              src: url('${fontUrl}') format('${this.getFontFormat(font.mimeType || 'truetype')}');
              font-weight: normal;
              font-style: normal;
              font-display: swap;
            }
          `;
          document.head.appendChild(style);

          const index = this.customFonts.findIndex(f => f.name === font.name);
          if (index !== -1) {
            this.customFonts[index].isLoading = false;
            this.customFonts[index].url = fontUrl;
          }
          console.log(`Font loaded from API: ${font.name}`);
        } else {
          const index = this.customFonts.findIndex(f => f.name === font.name);
          if (index !== -1) this.customFonts[index].isLoading = false;
        }
      });
  }

  private getFontFormat(mimeType: string): string {
    const formatMap: { [key: string]: string } = {
      'font/ttf': 'truetype',
      'application/x-font-ttf': 'truetype',
      'font/otf': 'opentype',
      'application/x-font-otf': 'opentype',
      'font/woff': 'woff',
      'application/font-woff': 'woff',
      'font/woff2': 'woff2',
      'application/font-woff2': 'woff2',
      'application/octet-stream': 'woff2' // Default for octet-stream
    };
    return formatMap[mimeType] || 'truetype';
  }

  private getTitle(state: any, parent: any): string[] {
    let data: string[] = [];
    if (parent?.snapshot?.data?.Title) {
      data.push(parent.snapshot.data.Title);
    }
    if (state && parent) {
      data.push(...this.getTitle(state, state.firstChild(parent)));
    }
    return data;
  }

  private getMeta(state: any, parent: any): string[] {
    let data: string[] = [];
    if (parent?.snapshot?.data?.meta) {
      data.push(parent.snapshot.data.meta);
    }
    if (state && parent) {
      data.push(...this.getMeta(state, state.firstChild(parent)));
    }
    return data;
  }

  private navigationInterceptor(event: any): void {
    if (event instanceof NavigationStart) {
      this.loadingDataImg = true;
    }
    if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
      this.loadingDataImg = false;
    }
  }

  private preloadAssets() {
    if (typeof Image === 'undefined') return;
    const assets = ['assets/img/icon/logo.png', 'assets/img/icon/favicon.png', 'assets/img/icon/shortcut.svg'];
    let loaded = 0;
    assets.forEach(url => {
      const img = new Image();
      img.src = url;
      img.onload = () => {
        loaded++;
        this.loadingService.setAssetProgress(loaded, assets.length);
      };
    });
  }

  private handleBodyHeroClass(url: string): void {
    // Check if current route is homepage
    this.isHomepage = url === '/' || url === '';

    this.isUnauthorized = url === '/unauthorized' || url === 'unauthorized';
    this.bodyClassService.setHeroMode(this.isHomepage);
  }
}